import { decodeBase64Url, encodeLabelsToBase64Url } from './bitEncoding';
import { useStore } from './store';
import { isTaskQualityType } from './taskQuality';
import { showToast } from './toast';
import type { TaskQualityType } from './types';

export interface AnnotationDraft {
  operator: string;
  episodeId: string;
  deviceTypeEnum: number | null;
  leaseId: string | null;
  encoded: string;
  numSeconds: number;
  batchIndex: number;
  batchOffset: number;
  visitedSeconds: number[];
  savedAt: number;
  markStatus?: string;
  nullFrameIndices?: number[];
  taskQualityType?: TaskQualityType;
}

const DRAFT_PREFIX = 'secshot_draft_v1_';
const DRAFT_INDEX_PREFIX = 'secshot_draft_index_v1_';
const AUTOSAVE_MS = 5000;

let autosaveTimer: ReturnType<typeof setInterval> | null = null;
let beforeUnloadHooked = false;
let draftDirty = false;
let lastSavedAt = 0;
let pendingRestoredStartSecond: number | null = null;
let lastRestoredStartSecond: number | null = null;

function draftKey(
  operator: string | null | undefined,
  episodeId: string | null | undefined
): string | null {
  const name = (operator || '').trim();
  const episode = (episodeId || '').trim();
  if (!name || !episode) return null;
  return `${DRAFT_PREFIX}${name}_${episode}`;
}

function draftIndexKey(operator: string | null | undefined): string | null {
  const name = (operator || '').trim();
  if (!name) return null;
  return `${DRAFT_INDEX_PREFIX}${name}`;
}

function safeParse(raw: string | null): AnnotationDraft | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AnnotationDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.operator || !parsed.episodeId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function loadDraftForOperator(
  operator: string | null | undefined,
  episodeId?: string | null
): AnnotationDraft | null {
  let targetEpisode = episodeId ?? null;
  if (!targetEpisode) {
    const indexKey = draftIndexKey(operator);
    if (indexKey) {
      try {
        const stored = localStorage.getItem(indexKey);
        if (stored) targetEpisode = stored;
      } catch {
        // ignore
      }
    }
  }
  const key = draftKey(operator, targetEpisode);
  if (!key) return null;
  try {
    return safeParse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

export function clearDraftForOperator(
  operator: string | null | undefined,
  episodeId?: string | null
): void {
  const key = draftKey(operator, episodeId ?? null);
  if (!key) return;
  try {
    localStorage.removeItem(key);
    const indexKey = draftIndexKey(operator);
    if (indexKey) {
      const current = localStorage.getItem(indexKey);
      if (!episodeId || current === episodeId) {
        localStorage.removeItem(indexKey);
      }
    }
  } catch {
    // ignore
  }
  draftDirty = false;
  lastSavedAt = Date.now();
}

function decodeLabels(
  encoded: string,
  numSeconds: number,
  images: string[],
  nullFrameIndices?: number[]
): Record<string, boolean | null> {
  const labels: Record<string, boolean | null> = {};
  const nullSet = new Set(nullFrameIndices ?? []);
  const clampedSeconds = Math.min(Math.max(0, numSeconds), images.length);
  if (!encoded || clampedSeconds === 0) {
    images.forEach((imgName, idx) => {
      labels[imgName] = nullSet.has(idx) ? null : false;
    });
    return labels;
  }
  let data: Uint8Array | null = null;
  try {
    data = decodeBase64Url(encoded);
  } catch {
    data = null;
  }
  images.forEach((imgName, idx) => {
    if (nullSet.has(idx)) {
      labels[imgName] = null;
      return;
    }
    if (idx >= clampedSeconds || !data) {
      labels[imgName] = false;
      return;
    }
    const byteIndex = Math.floor(idx / 8);
    const bitIndex = idx % 8;
    const byte = data[byteIndex] ?? 0;
    labels[imgName] = ((byte >> bitIndex) & 1) === 1;
  });
  return labels;
}

export function applyDraftIfAvailable(): boolean {
  const state = useStore.getState();
  if (state.currentEpisodeStatus === 'completed') return false;
  const draft = loadDraftForOperator(state.operator, state.selectedEpisode);
  if (!draft) return false;
  if (!state.selectedEpisode || draft.episodeId !== state.selectedEpisode) return false;
  if (state.images.length === 0) return false;

  const draftLabels = decodeLabels(
    draft.encoded,
    draft.numSeconds,
    state.images,
    draft.nullFrameIndices
  );
  const batchIndex = Math.max(
    0,
    Math.min(draft.batchIndex || 0, Math.ceil(state.images.length / 4))
  );
  const batchOffset = Math.max(0, Math.min(draft.batchOffset || 0, state.images.length - 1));
  const visited = new Set(
    (draft.visitedSeconds || []).filter(
      (idx) => Number.isFinite(idx) && idx >= 0 && idx < state.images.length
    )
  );
  // Only override labels for frames the user has explicitly visited.
  // Unvisited frames keep the current default state so stale drafts
  // don't overwrite frames the user hasn't reviewed yet.
  const mergedLabels = { ...state.labels };
  for (const idx of visited) {
    const imgName = state.images[idx];
    if (imgName !== undefined) {
      const draftVal = draftLabels[imgName];
      if (draftVal !== undefined) mergedLabels[imgName] = draftVal;
    }
  }
  const leaseId = draft.leaseId ?? state.leaseId;
  let selectedDeviceTypeEnum = state.selectedDeviceTypeEnum;
  if (typeof draft.deviceTypeEnum === 'number' || draft.deviceTypeEnum === null) {
    selectedDeviceTypeEnum = draft.deviceTypeEnum;
  }

  useStore.setState({
    labels: mergedLabels,
    batchIndex,
    batchOffset,
    visited,
    leaseId,
    selectedDeviceTypeEnum,
    conflictFrames: new Set<string>(),
    ...(isTaskQualityType(draft.taskQualityType) && {
      currentEpisodeTaskQualityType: draft.taskQualityType,
      currentEpisodeTaskQualityConflict: false,
    }),
  });

  if (lastRestoredStartSecond === null) {
    lastRestoredStartSecond = batchIndex * 4 + batchOffset;
  }
  showToast('Restored your in-progress annotation.');
  draftDirty = false;
  return true;
}

export function saveDraftSnapshot(): void {
  const state = useStore.getState();
  if (state.currentEpisodeStatus === 'completed') return;
  const key = draftKey(state.operator, state.selectedEpisode);
  if (!key) return;
  if (!state.selectedEpisode || state.images.length === 0) return;
  const { encoded, numSeconds } = encodeLabelsToBase64Url(state.images, state.labels);
  const nullFrameIndices = state.images
    .map((imgName, idx) => (state.labels[imgName] === null ? idx : -1))
    .filter((idx) => idx >= 0);
  const draft: AnnotationDraft = {
    operator: state.operator,
    episodeId: state.selectedEpisode,
    deviceTypeEnum: state.selectedDeviceTypeEnum,
    leaseId: state.leaseId,
    encoded,
    numSeconds,
    batchIndex: state.batchIndex,
    batchOffset: state.batchOffset,
    visitedSeconds: Array.from(state.visited),
    savedAt: Date.now(),
    markStatus: state.currentEpisodeStatus ?? 'pending',
    ...(isTaskQualityType(state.currentEpisodeTaskQualityType) && {
      taskQualityType: state.currentEpisodeTaskQualityType,
    }),
    ...(nullFrameIndices.length > 0 && { nullFrameIndices }),
  };
  try {
    localStorage.setItem(key, JSON.stringify(draft));
    const indexKey = draftIndexKey(state.operator);
    if (indexKey) {
      localStorage.setItem(indexKey, state.selectedEpisode);
    }
    draftDirty = false;
    lastSavedAt = Date.now();
  } catch {
    // ignore write failures
  }
}

export function startDraftAutosave(): void {
  if (autosaveTimer !== null) return;
  autosaveTimer = setInterval(() => {
    if (draftDirty) {
      saveDraftSnapshot();
    }
  }, AUTOSAVE_MS);
  if (!beforeUnloadHooked) {
    window.addEventListener('beforeunload', saveDraftSnapshot);
    beforeUnloadHooked = true;
  }
}

export function stopDraftAutosave(): void {
  if (autosaveTimer !== null) {
    clearInterval(autosaveTimer);
    autosaveTimer = null;
  }
}

export function markDraftDirty(): void {
  const state = useStore.getState();
  if (state.currentEpisodeStatus === 'completed') return;
  draftDirty = true;
}

export function getDraftStatus(): { dirty: boolean; lastSavedAt: number } {
  return { dirty: draftDirty, lastSavedAt };
}

export function getRestoredStartSecond(): number | null {
  return lastRestoredStartSecond;
}

export function setPendingRestoredStartSecond(sec: number | null): void {
  pendingRestoredStartSecond = sec;
  if (sec != null) {
    lastRestoredStartSecond = sec;
  }
}

export function consumeRestoredStartSecond(): number | null {
  const v = pendingRestoredStartSecond ?? lastRestoredStartSecond;
  pendingRestoredStartSecond = null;
  lastRestoredStartSecond = null;
  return v;
}
